"use client"

import { useEffect, useMemo, useRef, useState } from "react"
import { ChevronLeft, Eye, EyeOff, Phone, QrCode, Sparkles, Upload, CheckCircle2 } from "lucide-react"
import axios, { isAxiosError } from "axios"
import { Bounce, toast } from "react-toastify"
import { useDispatch } from "react-redux"
import { AppDispatch } from '../store';
import { loginOrganization } from "../Actions/Organization/AuthActions"
import OrganizationAxiosInstance from "../Services/OrganizationAxiosInstance";

type BrandColors = {
  primary: string
  secondary: string
  accent: string
  textOnPrimary: string
  logoPlate: string
  pointIconColor: string
}

type RGB = {
  r: number
  g: number
  b: number
}

type StepKey =
  | "business"
  | "logo"
  | "starterPreview"
  | "reward"
  | "signup"
  | "final"

type IconKeyword =
  | "coffee"
  | "utensils"
  | "cake"
  | "scissors"
  | "dumbbell"
  | "cross"
  | "shirt"
  | "gem"
  | "flower"
  | "shopping-basket"
  | "smartphone"
  | "car"
  | "store"
  | "sparkles"

type Folder = "logos" | "icons" | "points" | "StripeBackgroundImage"

const STEPS: StepKey[] = [
  "business",
  "logo",
  "starterPreview",
  "reward",
  "signup",
  "final",
]

// ── Analytics helpers ──
async function sha256(str: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(str.trim().toLowerCase()))
  return Array.from(new Uint8Array(buf)).map(b => b.toString(16).padStart(2, '0')).join('')
}

const STEP_NUMBERS: Record<string, number> = {
  business: 1, logo: 2, starterPreview: 3, reward: 4, signup: 5, final: 6
}

function pushEvent(name: string, params?: Record<string, unknown>) {
  const w = window as any
  w.dataLayer = w.dataLayer || []
  w.dataLayer.push({ event: name, ...params })
}

export default function Page() {
  const dispatch = useDispatch<AppDispatch>()
  const inputRef = useRef<HTMLInputElement | null>(null)

 
  const [isProcessing, setIsProcessing] = useState(false)
  const [isBuildingProgram, setIsBuildingProgram] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [phone, setPhone] = useState("")
  const [businessName, setBusinessName] = useState("")
  const [businessType, setBusinessType] = useState("")
  const [pointsToCollect, setPointsToCollect] = useState(8)
  const [rewardText, setRewardText] = useState("منتج مجاني")
 const [step,setStep] = useState<StepKey>("business")
  const [originalSrc, setOriginalSrc] = useState<string | null>(null)
  const [processedLogoSrc, setProcessedLogoSrc] = useState<string | null>(null)
  const [colors, setColors] = useState<BrandColors | null>(null)
const [wideLogoPreviewSrc, setWideLogoPreviewSrc] = useState<string | null>(null)
  const [signupName, setSignupName] = useState("")
  
  const [signupEmail, setSignupEmail] = useState("")
  const [signupPhone, setSignupPhone] = useState(phone)
  const [signupPassword, setSignupPassword] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [agree, setAgree] = useState(false)
  const [signupErrors, setSignupErrors] = useState<Record<string, string>>({})
  const [programId, setProgramId] = useState<string>("")
  function validateSignupForm() {
  const nextErrors: Record<string, string> = {}

  if (!signupName.trim()) {
    nextErrors.name = "الاسم مطلوب"
  } else if (signupName.trim().length < 3) {
    nextErrors.name = "الاسم قصير جدًا"
  }

  if (!businessName.trim()) nextErrors.businessName = "اسم النشاط مطلوب"

  if (!signupEmail.trim()) {
    nextErrors.email = "البريد الإلكتروني مطلوب"
  } else if (!/\S+@\S+\.\S+/.test(signupEmail)) {
    nextErrors.email = "صيغة البريد الإلكتروني غير صحيحة"
  }

  if (!signupPhone.trim()) {
    nextErrors.phone = "رقم الجوال مطلوب"
  } else if (!/^[0-9]{8,15}$/.test(signupPhone.replace(/\+/g, ""))) {
    nextErrors.phone = "رقم الجوال يجب أن يكون بين 8 و 15 رقم"
  }

  if (!signupPassword) {
    nextErrors.password = "كلمة المرور مطلوبة"
  } else if (signupPassword.length < 8) {
    nextErrors.password = "كلمة المرور يجب ألا تقل عن 8 أحرف"
  }

  if (!agree) {
    nextErrors.global = "يجب الموافقة على الشروط والسياسات قبل المتابعة"
  }

  setSignupErrors(nextErrors)
  return Object.keys(nextErrors).length === 0
}
function splitFullName(fullName: string): { firstName: string; lastName: string } {
  const cleaned = fullName.trim().replace(/\s+/g, " ")
  if (!cleaned) {
    return { firstName: "", lastName: "" }
  }

  const parts = cleaned.split(" ")
  const firstName = parts[0] || ""
  const lastName = parts.slice(1).join(" ").trim() || firstName

  return { firstName, lastName }
}
  const englishBusinessType = useMemo(
    () => translateArabicBusinessTypeToEnglish(businessType),
    [businessType]
  )

  const iconKeyword = useMemo<IconKeyword>(
    () => pickBestIconKeyword(englishBusinessType),
    [englishBusinessType]
  )

  const pointIconSvg = useMemo(
    () =>
      buildPointIconDataUrl(
        iconKeyword,
        colors?.pointIconColor || "#111827",
        64,
        64
      ),
    [iconKeyword, colors]
  )

  useEffect(() => {
    if (!colors && processedLogoSrc) {
      setProcessedLogoSrc(null)
    }
  }, [colors, processedLogoSrc])

  

  async function handleFile(file: File | null): Promise<void> {
    if (!file) return

    setIsProcessing(true)
    setError(null)

    try {
      if (!file.type.startsWith("image/")) {
        throw new Error("ارفع صورة بصيغة PNG أو JPG أو WebP فقط.")
      }

      const objectUrl = URL.createObjectURL(file)
      setOriginalSrc(objectUrl)

      const originalImage = await loadImage(objectUrl)
      const extractedColors = await extractSmartBrandColors(originalImage)
      const croppedLogoSrc = cropLogoPaddingToDataUrl(originalImage)
const wideLogoPreviewFile = await renderContainedImageFile(
  croppedLogoSrc,
  480,
  150,
  "logo-preview.jpg",
  extractedColors.primary,
  "image/jpeg",
  0.92,
  "left"
)

const wideLogoPreviewUrl = URL.createObjectURL(wideLogoPreviewFile)
      setColors(extractedColors)
      setProcessedLogoSrc(croppedLogoSrc)
      setStep("starterPreview")
    } catch (err) {
      console.error(err)
      setError(err instanceof Error ? err.message : "فشل تجهيز الشعار.")
      setOriginalSrc(null)
      setProcessedLogoSrc(null)
      setColors(null)
    } finally {
      setIsProcessing(false)
    }
  }

  // Fire onboarding_started once on mount
  useEffect(() => {
    pushEvent('onboarding_started')
  }, [])

  function nextStep() {
    pushEvent('onboarding_step_completed', {
      step_name: step,
      step_number: STEP_NUMBERS[step] ?? 0,
    })
    const currentIndex = STEPS.findIndex((item) => item === step)
    const next = STEPS[currentIndex + 1]
    if (next) setStep(next)
  }

  function prevStep() {
    const currentIndex = STEPS.findIndex((item) => item === step)
    const prev = STEPS[currentIndex - 1]
    if (prev) setStep(prev)
  }

  function canContinue(): boolean {

    if (step === "business") {
      return businessName.trim().length >= 2 && businessType.trim().length >= 2
    }

    if (step === "logo") return !!processedLogoSrc && !!colors

    if (step === "starterPreview") return !!processedLogoSrc && !!colors

    if (step === "reward") {
      return pointsToCollect >= 3 && rewardText.trim().length >= 2
    }

    if (step === "signup") return false
    if (step === "final") return true

    return false
  }

  

  async function loginAfterSignup(email: string, password: string) {
    const actionResponse = await dispatch(loginOrganization(email, password))
    if (!actionResponse.success) {
      throw new Error(actionResponse.message || "فشل تسجيل الدخول بعد إنشاء الحساب")
    }
  }

  async function signUpOrganization() {
    const { firstName, lastName } = splitFullName(signupName)
    const response = await axios.post(
      `${process.env.NEXT_PUBLIC_HOST || process.env.REACT_APP_HOST}OrganizationAuth/signup`,
      {
        firstName: firstName,
        lastName: lastName,
        businessName,
        email: signupEmail,
        phone: signupPhone,
        password: signupPassword,
      }
    )

    if (response.status !== 200) {
      throw new Error("فشل إنشاء الحساب")
    }
  }

  async function createPassTemplate(
    name: string,
    description: string,
    points: number,
    rewardName: string
  ): Promise<string> {
    const response = await OrganizationAxiosInstance.post(`/PassTemplate`, {
      Name: name,
      Description: description,
      Rewardname: rewardName,
      PointsToCollect: points,
    })

    if (response.status !== 200 || !response.data?.id) {
      throw new Error("فشل إنشاء البرنامج")
    }

    return response.data.id as string
  }

  async function updatePassTemplateDesign(passTemplateId: string, payload: {
    backgroundColor: string
    textColor: string
    stripeBackgroundColor: string
    stripeBackgroundImage: string
    icon: string
    logo: string
    pointImage: string
  }) {
    const response = await OrganizationAxiosInstance.put(
      `/PassTemplate/${passTemplateId}/Design`,
      {
        BackgroundColor: payload.backgroundColor,
        TextColor: payload.textColor,
        StripeBackgroundColor: payload.stripeBackgroundColor,
        stripeBackgroundImage: payload.stripeBackgroundImage,
        Icon: payload.icon,
        logo: payload.logo,
        pointImage: payload.pointImage,
      }
    )

    if (response.status !== 200) {
      throw new Error("فشل حفظ تصميم البطاقة")
    }
  }

  async function activatePassTemplate(passTemplateId: string) {
    const response = await OrganizationAxiosInstance.get(
      `/PassTemplate/${passTemplateId}/Activate`
    )

    if (response.status !== 200) {
      throw new Error("فشل تفعيل البرنامج")
    }
  }

  async function getImageDimensions(file: File): Promise<{ width: number; height: number }> {
    return new Promise((resolve, reject) => {
      if (!file.type.startsWith("image/")) {
        reject(new Error("Selected file is not an image."))
        return
      }

      const img = new window.Image()
      img.onload = () => {
        URL.revokeObjectURL(img.src)
        resolve({ width: img.width, height: img.height })
      }
      img.onerror = () => {
        URL.revokeObjectURL(img.src)
        reject(new Error("Could not read image."))
      }
      img.src = URL.createObjectURL(file)
    })
  }

  async function validateImageForFolder(file: File, folder: Folder): Promise<void> {
    const okType = file.type === "image/png" || file.type === "image/jpeg"
    if (!okType) throw new Error("نوع الصورة يجب أن يكون PNG أو JPG")

    const limits: Record<Folder, { w: number; h: number; label: string }> = {
      logos: { w: 480, h: 150, label: "480x150px" },
      icons: { w: 87, h: 87, label: "87x87px" },
      StripeBackgroundImage: { w: 1125, h: 432, label: "1125x432px" },
      points: { w: 200, h: 200, label: "200x200px" },
    }

    const { w, h, label } = limits[folder]
    const { width, height } = await getImageDimensions(file)

    if (width > w || height > h) {
      throw new Error(`مقاس الصورة كبير. الحد الأقصى ${label}`)
    }
  }

  async function uploadImage(img: File, folder: Folder): Promise<string> {
    await validateImageForFolder(img, folder)

    const formData = new FormData()
    formData.append("Img", img)
    formData.append("Folder", folder)

    const response = await OrganizationAxiosInstance.post(`/Images/`, formData, {
      headers: { "Content-Type": "multipart/form-data" },
    })

    if (response.status !== 200) {
      throw new Error("فشل رفع الصورة")
    }

    return extractUploadedImageValue(response.data)
  }

  function extractUploadedImageValue(data: any): string {
    if (!data) return ""
    if (typeof data === "string") return data
    if (typeof data?.url === "string") return data.url
    if (typeof data?.path === "string") return data.path
    if (typeof data?.imageUrl === "string") return data.imageUrl
    if (typeof data?.value === "string") return data.value
    return ""
  }

  async function buildAssetFiles() {
    if (!processedLogoSrc || !colors) {
      throw new Error("ملفات التصميم غير جاهزة")
    }

    const logoWide = await renderContainedImageFile(
      processedLogoSrc,
      480,
      150,
      "logo.jpg",
      colors.primary,
      "image/jpeg",
      0.92,
      "left"
    )

    const logoIcon = await renderContainedImageFile(
      processedLogoSrc,
      87,
      87,
      "icon.jpg",
      "#ffffff",
      "image/jpeg",
      0.92
    )

    const pointIcon = await renderContainedImageFile(
      pointIconSvg,
      200,
      200,
      "point.jpg",
      colors.accent,
      "image/jpeg",
      0.92
    )

    return {
      logoWide,
      logoIcon,
      pointIcon,
    }
  }

  async function handleSignupAndBuild(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()

    if (!validateSignupForm()) return
    if (!colors || !processedLogoSrc) {
      setSignupErrors({ global: "الهوية غير جاهزة بعد. ارجع خطوة وتأكد من رفع الشعار." })
      return
    }

    const toastId = toast.loading("جارٍ إنشاء الحساب وتجهيز البرنامج...", {
      position: "top-right",
      theme: "light",
      isLoading: true,
      transition: Bounce,
    })

    setIsBuildingProgram(true)

    // Fire signup submitted event with hashed PII
    sha256(signupEmail).then(hashedEmail =>
      sha256(signupPhone).then(hashedPhone =>
        pushEvent('onboarding_signup_submitted', {
          business_name: businessName,
          business_type: businessType,
          hashed_email: hashedEmail,
          hashed_phone: hashedPhone,
        })
      )
    )

    try {
      await signUpOrganization()
      await loginAfterSignup(signupEmail, signupPassword)

      const createdPassTemplateId = await createPassTemplate(
        businessName,
        businessType,
        pointsToCollect,
        rewardText
      )

      pushEvent('onboarding_program_created', {
        business_name: businessName,
        program_id: createdPassTemplateId,
      })

      const assets = await buildAssetFiles()

      const uploadedLogo = await uploadImage(assets.logoWide, "logos")
      const uploadedIcon = await uploadImage(assets.logoIcon, "icons")
      const uploadedPoint = await uploadImage(assets.pointIcon, "points")

      await updatePassTemplateDesign(createdPassTemplateId, {
        backgroundColor: colors.primary,
        textColor: colors.textOnPrimary,
        stripeBackgroundColor: colors.accent,
        stripeBackgroundImage: "",
        icon: uploadedIcon,
        logo: uploadedLogo,
        pointImage: uploadedPoint,
      })

      await activatePassTemplate(createdPassTemplateId)

      // Re-login to get a fresh JWT that includes the new passTemplate in its claims
      await loginAfterSignup(signupEmail, signupPassword)

      setProgramId(createdPassTemplateId)
      setStep("final")
      pushEvent('onboarding_completed', {
        business_name: businessName,
        program_id: createdPassTemplateId,
      })

      toast.update(toastId, {
        render: "تم إنشاء البرنامج وتفعيله بنجاح",
        type: "success",
        isLoading: false,
        autoClose: 3000,
      })
    } catch (err) {
      console.error(err)

      const message =
        isAxiosError(err)
          ? err.response?.data?.error || "حدث خطأ أثناء تجهيز البرنامج"
          : err instanceof Error
          ? err.message
          : "حدث خطأ أثناء تجهيز البرنامج"

      setSignupErrors({ global: message })

      pushEvent('onboarding_error', {
        step_name: 'signup',
        error_message: message,
      })

      toast.update(toastId, {
        render: message,
        type: "error",
        isLoading: false,
        autoClose: 4000,
      })
    } finally {
      setIsBuildingProgram(false)
    }
  }

  const showPreview = step === "starterPreview"

  return (
    <main
      dir="rtl"
      className="min-h-screen bg-[linear-gradient(180deg,#f7f8fc_0%,#f5f6fb_45%,#ffffff_100%)] px-3 py-4 sm:px-4"
    >
      <div className="mx-auto max-w-md">
        <div className="px-4 py-5 sm:px-5 sm:py-6">
          <div className="mb-1 inline-flex items-center gap-2 rounded-full border border-[#dfe3f0] bg-[#f7f8fc] px-3 py-1.5 text-[11px] font-bold text-[#5e6577]">
            <Sparkles className="h-4 w-4" />
            سنجهز برنامج الولاء خلال أقل من 5 دقائق
          </div>
        </div>

        <section className="overflow-hidden rounded-[28px] border border-[#e6e8f2] bg-white p-4 shadow-[0_10px_35px_rgba(17,17,17,0.05)] sm:p-5">
        

          {step === "business" && (
  <StepShell
    title="خلنا نجهز هويتك أولًا"
    description="ابدأ باسم النشاط ونوعه، وبعدها سنجهز لك بطاقة ولاء مبدئية بسرعة."
  >
    

    <Field label="اسم النشاط">
      <input
        value={businessName}
        onChange={(e) => setBusinessName(e.target.value)}
        placeholder="مثال: كوفي رؤى"
        className="w-full rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4 py-3 text-sm outline-none transition focus:border-[rgb(166,175,216)]"
      />
    </Field>

    <Field label="نوع النشاط">
      <input
        value={businessType}
        onChange={(e) => setBusinessType(e.target.value)}
        placeholder="مثال: مقهى، مطعم، صالون، متجر"
        className="w-full rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4 py-3 text-sm outline-none transition focus:border-[rgb(166,175,216)]"
      />
    </Field>

    
  </StepShell>
)}

          {step === "logo" && (
            <StepShell
              title="ارفع شعار النشاط"
              description="ارفع الشعار كصورة فقط، وسنجهز لك المعاينة تلقائيًا."
            >
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="flex w-full items-center justify-center gap-3 rounded-[24px] border-2 border-dashed border-[#d8deef] bg-[#fafbff] px-4 py-7 transition hover:border-[rgb(166,175,216)] hover:bg-[#f5f7fd]"
              >
                <div className="rounded-2xl bg-white p-3 shadow-sm">
                  <Upload className="h-6 w-6 text-[#5f6678]" />
                </div>

                <div className="text-right">
                  <p className="text-base font-extrabold text-[#111111]">رفع الشعار</p>
                  <p className="mt-1 text-sm text-[#6b7280]">PNG أو JPG أو WebP</p>
                </div>
              </button>

              <input
                ref={inputRef}
                type="file"
                accept="image/png,image/jpeg,image/jpg,image/webp"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0] ?? null)}
              />

              {isProcessing && (
                <InfoBox text="جارٍ تحليل الشعار وتجهيز المعاينة..." />
              )}

              {error && <ErrorBox text={error} />}

              {(originalSrc || processedLogoSrc) && (
                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <PreviewBox title="الصورة المرفوعة">
                    {originalSrc ? (
                      <img
                        src={originalSrc}
                        alt="الملف الأصلي"
                        className="max-h-28 max-w-full object-contain"
                      />
                    ) : null}
                  </PreviewBox>

                  <PreviewBox title="الشعار بعد المعالجة">
                    {processedLogoSrc ? (
                      <div className="grid h-full w-full place-items-center rounded-2xl bg-[radial-gradient(circle,_#e4e4e7_1px,_transparent_1px)] [background-size:12px_12px]">
                        <img
                          src={processedLogoSrc}
                          alt="الشعار بعد المعالجة"
                          className="max-h-28 max-w-full object-contain"
                        />
                      </div>
                    ) : null}
                  </PreviewBox>
                </div>
              )}
            </StepShell>
          )}

          {step === "starterPreview" && (
            <StepShell
              title="هذه المعاينة الأولى"
              description="عرضنا لك البطاقة بشكل مبدئي مع 4 نقاط حتى تتأكد من الهوية والألوان."
            >
              {showPreview && colors && processedLogoSrc && (
                <div className="mb-5 flex justify-center">
                  <PhonePassPreview
                    logoSrc={processedLogoSrc}
                    businessTypeText={businessType}
                    colors={colors}
                    pointIconSrc={pointIconSvg}
                    pointsToCollect={4}
                    points={4}
                    redeemedRewards={0}
                  />
                </div>
              )}
              {wideLogoPreviewSrc && (
  <div className="mt-4 rounded-3xl border border-[#e5e7eb] p-4">
    <p className="mb-3 text-sm font-bold text-[#111111]">
      الشعار العريض الذي سيتم استخدامه
    </p>

    <div className="flex justify-center rounded-2xl bg-[#fafbff] p-4">
      <img
        src={wideLogoPreviewSrc}
        alt="Wide logo preview"
        className="max-w-full rounded-xl border border-[#e5e7eb]"
      />
    </div>
  </div>
)}

              <InfoBox text="هذا مجرد تصميم مبدئي لتشغيل البرنامج بسرعة. يمكنك تغيير الألوان، الشعار، والتصميم لاحقًا في أي وقت." />
            </StepShell>
          )}

          {step === "reward" && (
            <StepShell
              title="حدد نظام المكافأة"
              description="كم نقطة يحتاج العميل للحصول على الهدية؟ وما هي الهدية؟"
            >
              <Field label="عدد النقاط المطلوبة للحصول على الهدية">
                <input
                  type="number"
                  min={3}
                  max={30}
                  value={pointsToCollect}
                  onChange={(e) => {
                    const value = Number.parseInt(e.target.value || "8", 10)
                    setPointsToCollect(
                      Number.isNaN(value) ? 8 : Math.max(3, Math.min(30, value))
                    )
                  }}
                  className="w-full rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4 py-3 text-sm outline-none transition focus:border-[rgb(166,175,216)]"
                />
              </Field>

              <Field label="ما هي الهدية؟">
                <input
                  value={rewardText}
                  onChange={(e) => setRewardText(e.target.value)}
                  placeholder="مثال: قهوة مجانية أو خصم 20%"
                  className="w-full rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4 py-3 text-sm outline-none transition focus:border-[rgb(166,175,216)]"
                />
              </Field>
            </StepShell>
          )}

          {step === "signup" && (
            <StepShell
              title="أنشئ حسابك وأطلق البرنامج"
              description="بعد التسجيل سننشئ البرنامج، نرفع الصور، نحفظ التصميم، ثم نفعّل البطاقة تلقائيًا."
            >
              <form className="space-y-4" onSubmit={handleSignupAndBuild}>
                <div className="rounded-2xl border border-[#e5e7eb] bg-[#fafbff] p-4">
                  <p className="text-sm font-bold text-[#111111]">بيانات البرنامج</p>
                  <div className="mt-3 space-y-2 text-sm text-[#5f6678]">
                    <p><span className="font-bold text-[#111111]">اسم النشاط:</span> {businessName}</p>
                    <p><span className="font-bold text-[#111111]">نوع النشاط:</span> {businessType}</p>
                    <p><span className="font-bold text-[#111111]">عدد النقاط:</span> {pointsToCollect}</p>
                    <p><span className="font-bold text-[#111111]">الهدية:</span> {rewardText}</p>
                  </div>
                </div>

                <Field label="الاسم *">
                  <input
                    type="text"
                    value={signupName}
                    onChange={(e) => setSignupName(e.target.value)}
                    placeholder="مثال: عبدالله محمد"
                    className="w-full rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4 py-3 text-sm outline-none transition focus:border-[rgb(166,175,216)]"
                  />
                  {signupErrors.name && (
                    <p className="mt-2 text-sm text-red-600">{signupErrors.name}</p>
                  )}
                </Field>

                <Field label="رقم الجوال *">
                  <input
                    type="tel"
                    value={signupPhone}
                    onChange={(e) => setSignupPhone(e.target.value)}
                    placeholder="مثال: 0501234567"
                    className="w-full rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4 py-3 text-sm outline-none transition focus:border-[rgb(166,175,216)]"
                  />
                  {signupErrors.phone && (
                    <p className="mt-2 text-sm text-red-600">{signupErrors.phone}</p>
                  )}
                </Field>

                <Field label="البريد الإلكتروني *">
                  <input
                    type="email"
                    value={signupEmail}
                    onChange={(e) => setSignupEmail(e.target.value)}
                    className="w-full rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4 py-3 text-sm outline-none transition focus:border-[rgb(166,175,216)]"
                  />
                  {signupErrors.email && (
                    <p className="mt-2 text-sm text-red-600">{signupErrors.email}</p>
                  )}
                </Field>

                

                <Field label="كلمة المرور *">
                  <div className="flex items-center rounded-2xl border border-[#dde1ee] bg-[#fafbff] px-4">
                    <input
                      type={showPassword ? "text" : "password"}
                      value={signupPassword}
                      onChange={(e) => setSignupPassword(e.target.value)}
                      className="w-full bg-transparent py-3 text-sm outline-none"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="text-[#6b7280]"
                    >
                      {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                  {signupErrors.password && (
                    <p className="mt-2 text-sm text-red-600">{signupErrors.password}</p>
                  )}
                </Field>

                <div className="flex items-start gap-3 rounded-2xl border border-[#e5e7eb] bg-[#fafbff] p-4">
                  <input
                    id="agree"
                    type="checkbox"
                    checked={agree}
                    onChange={(e) => setAgree(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300"
                  />
                  <label htmlFor="agree" className="text-sm text-[#5f6678]">
                    أوافق على{" "}
                    <a
                      href="/Terms-Conditions"
                      target="_blank"
                      className="font-bold text-[#6d7697]"
                    >
                      الشروط والأحكام
                    </a>
                    {"، "}
                    <a
                      href="/Privacy-Policy"
                      target="_blank"
                      className="font-bold text-[#6d7697]"
                    >
                      سياسة الخصوصية
                    </a>
                    {" و "}
                    <a
                      href="/Fair-Use-Policy"
                      target="_blank"
                      className="font-bold text-[#6d7697]"
                    >
                      سياسة الاستخدام العادل
                    </a>
                  </label>
                </div>

                {signupErrors.global && <ErrorBox text={signupErrors.global} />}

                <button
                  type="submit"
                  disabled={isBuildingProgram}
                  className="w-full rounded-2xl bg-[#111111] px-4 py-3 text-sm font-extrabold text-white transition hover:-translate-y-[1px] hover:shadow-lg disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isBuildingProgram
                    ? "جارٍ إنشاء الحساب وتجهيز البرنامج..."
                    : "إنشاء الحساب وإطلاق البرنامج"}
                </button>
              </form>
            </StepShell>
          )}

          {step === "final" && (
            <StepShell
              title="تم تجهيز البرنامج بنجاح"
              description="تم إنشاء الحساب، تجهيز البطاقة، وتفعيل البرنامج."
            >
              <div className="flex flex-col items-center rounded-2xl border border-[#e5e7eb] bg-[#fafbff] p-6 text-center">
                <div className="mb-4 rounded-full bg-green-100 p-3 text-green-600">
                  <CheckCircle2 className="h-8 w-8" />
                </div>

                <p className="text-lg font-extrabold text-[#111111]">
                  برنامج الولاء جاهز الآن 🎉
                </p>


                <button
                  type="button"
                  onClick={() => {
                    if (programId) {
                      window.location.href = `/Dashboard/Program/${programId}/launch`
                    } else {
                      window.location.href = `/Dashboard`
                    }
                  }}
                  className="mt-6 w-full rounded-2xl bg-[#111111] px-4 py-3 text-sm font-extrabold text-white transition hover:-translate-y-[1px] hover:shadow-lg"
                >
                  جرب النظام
                </button>
              </div>
            </StepShell>
          )}

          {step !== "final" && (
            <div className="mt-6 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={prevStep}
                disabled={step === "business" || isBuildingProgram}
                className="inline-flex items-center gap-2 rounded-2xl border border-[#dde1ee] bg-white px-4 py-3 text-sm font-bold text-[#111111] disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ChevronLeft className="h-4 w-4 rotate-180" />
                السابق
              </button>

              {step !== "signup" && (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={!canContinue()}
                  className="rounded-2xl bg-[rgb(166,175,216)] px-5 py-3 text-sm font-extrabold text-[#111111] transition hover:-translate-y-[1px] hover:shadow-md disabled:cursor-not-allowed disabled:opacity-50"
                >
                  التالي
                </button>
              )}
            </div>
          )}
        </section>
      </div>
    </main>
  )
}

function StepShell({
  title,
  description,
  children,
}: {
  title: string
  description: string
  children: React.ReactNode
}) {
  return (
    <div>
      <h2 className="text-[1.45rem] font-black tracking-tight text-[#111111]">
        {title}
      </h2>
      <p className="mt-2 text-sm leading-7 text-[#5f6678]">{description}</p>
      <div className="mt-6 space-y-5">{children}</div>
    </div>
  )
}

function Field({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) {
  return (
    <div>
      <label className="mb-2 block text-sm font-bold text-[#111111]">{label}</label>
      {children}
    </div>
  )
}

function InfoBox({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-[#dbe3f6] bg-[#f7f9ff] px-4 py-3 text-sm text-[#44506b]">
      {text}
    </div>
  )
}

function ErrorBox({ text }: { text: string }) {
  return (
    <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
      {text}
    </div>
  )
}

function PreviewBox({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <div className="rounded-3xl border border-[#e5e7eb] p-4">
      <p className="mb-3 text-sm font-bold text-[#111111]">{title}</p>
      <div className="flex h-32 items-center justify-center rounded-2xl bg-[#fafbff]">
        {children}
      </div>
    </div>
  )
}

function PhonePassPreview({
  logoSrc,
  businessTypeText,
  colors,
  pointIconSrc,
  pointsToCollect,
  points,
  redeemedRewards,
}: {
  logoSrc: string
  businessTypeText: string
  colors: BrandColors
  pointIconSrc: string
  pointsToCollect: number
  points: number
  redeemedRewards: number
}) {
  const remainder = points % pointsToCollect
  const filledCount =
    remainder === 0 && points > 0 ? Math.min(points, pointsToCollect) : remainder

  const remainingPoints =
    remainder === 0 && points > 0
      ? Math.max(pointsToCollect - points, 0)
      : pointsToCollect - remainder

  const availableRewards = Math.max(
    0,
    Math.floor(points / pointsToCollect) - redeemedRewards
  )

  const columns = Math.min(pointsToCollect, 5)

  return (
    <div
      className="relative flex h-[455px] w-[220px] items-start bg-cover bg-top bg-no-repeat sm:h-[500px] sm:w-[245px]"
      style={{ backgroundImage: "url(/iphone.png)" }}
    >
      <div
        className="mx-4 mt-8 flex h-[66%] w-full flex-col overflow-hidden rounded"
        style={{ backgroundColor: colors.primary }}
      >
        <div className="flex justify-start px-2 pt-2">
         <div className="flex h-[56px] w-[82%] items-center justify-start overflow-hidden">
            <img
  src={logoSrc}
  alt="Business logo"
  className="h-full w-full object-contain object-left"
/>
          </div>
        </div>

        <div
          className="mt-2 flex w-full items-center justify-center px-2 py-2"
          style={{
            backgroundColor: colors.accent,
            height: "80px",
          }}
        >
          <div
            className="grid w-full items-center justify-items-center gap-x-1 gap-y-2"
            style={{
              gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
            }}
          >
            {Array.from({ length: pointsToCollect }).map((_, index) => {
              const isFilled = index < filledCount

              return (
                <img
                  key={index}
                  src={pointIconSrc}
                  alt="point icon"
                  className="h-8 w-8"
                  style={{ opacity: isFilled ? 1 : 0.22 }}
                />
              )
            })}
          </div>
        </div>

        <div className="flex flex-row justify-between px-2 pt-1">
          <div>
            <p
              style={{ fontSize: "6px", color: colors.textOnPrimary }}
              className="pb-0"
            >
              النقاط المتبقية
            </p>
            <p
              style={{ fontSize: "7px", color: colors.textOnPrimary }}
              className="pt-0 font-bold"
            >
              {remainingPoints}
            </p>
          </div>

          <div className="text-left">
            <p
              style={{ fontSize: "6px", color: colors.textOnPrimary }}
              className="pb-0"
            >
              الهدايا
            </p>
            <p
              style={{ fontSize: "7px", color: colors.textOnPrimary }}
              className="pt-0 font-bold"
            >
              {availableRewards}
            </p>
          </div>
        </div>

        <div className="mt-auto mb-2 flex justify-center">
          <div className="flex w-[45%] items-center justify-center rounded bg-white p-1">
            <QrCode className="h-auto w-full text-black" strokeWidth={2} />
          </div>
        </div>
      </div>

      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-white/90 px-3 py-1 text-[10px] font-bold text-zinc-700">
        {businessTypeText || "نشاط تجاري"}
      </div>
    </div>
  )
}

/* -------------------- file/image builders -------------------- */

async function renderContainedImageFile(
  src: string,
  width: number,
  height: number,
  filename: string,
  backgroundColor: string,
  mimeType: "image/jpeg" | "image/png",
  quality = 0.92,
  alignX: "left" | "center" = "center"
): Promise<File> {
  const img = await loadImage(src)
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")

  if (!ctx) throw new Error("تعذر تجهيز الصورة")

  canvas.width = width
  canvas.height = height

  ctx.fillStyle = backgroundColor
  ctx.fillRect(0, 0, width, height)

  const ratio = Math.min(width / img.width, height / img.height)
  const drawWidth = img.width * ratio
  const drawHeight = img.height * ratio
  const dx = alignX === "left" ? 0 : (width - drawWidth) / 2
  const dy = (height - drawHeight) / 2

  ctx.drawImage(img, dx, dy, drawWidth, drawHeight)

  const blob = await canvasToBlob(canvas, mimeType, quality)
  return new File([blob], filename, { type: mimeType })
}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  mimeType: string,
  quality?: number
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error("تعذر تحويل الصورة"))
        return
      }
      resolve(blob)
    }, mimeType, quality)
  })
}

/* -------------------- business type mapping -------------------- */

function translateArabicBusinessTypeToEnglish(input: string): string {
  const value = normalizeArabicText(input)

  const rules: Array<{ arabic: string[]; english: string }> = [
    { arabic: ["مقهى", "كوفي", "كافيه", "قهوة", "مختص"], english: "coffee shop" },
    { arabic: ["مطعم", "اكل", "ماكولات", "مأكولات", "وجبات", "برجر", "بيتزا"], english: "restaurant" },
    { arabic: ["حلويات", "حلى", "كيك", "مخبز", "مخبوزات"], english: "bakery" },
    { arabic: ["صالون", "حلاقة", "حلاق", "مشغل", "تجميل", "سبا"], english: "salon" },
    { arabic: ["نادي", "جيم", "رياضة", "لياقة", "فتنس", "تمارين"], english: "gym" },
    { arabic: ["صيدلية", "دواء", "عناية", "صحة"], english: "pharmacy" },
    { arabic: ["ملابس", "ازياء", "عبايات", "شنط", "احذيه", "أحذية", "بوتيك"], english: "fashion store" },
    { arabic: ["عطور", "عطر", "بخور"], english: "perfume store" },
    { arabic: ["مجوهرات", "ذهب", "فضة", "اكسسوارات", "إكسسوارات"], english: "jewelry store" },
    { arabic: ["ورد", "زهور", "هدايا"], english: "flower shop" },
    { arabic: ["بقالة", "سوبرماركت", "تموينات", "ميني ماركت"], english: "grocery store" },
    { arabic: ["الكترونيات", "إلكترونيات", "جوالات", "هواتف", "كمبيوتر"], english: "electronics store" },
    { arabic: ["سيارات", "مغسلة", "زينة سيارات", "ورشة", "كفرات"], english: "car service" },
  ]

  for (const rule of rules) {
    if (rule.arabic.some((keyword) => value.includes(normalizeArabicText(keyword)))) {
      return rule.english
    }
  }

  return input.trim() ? input.trim().toLowerCase() : "store"
}

function pickBestIconKeyword(englishBusinessType: string): IconKeyword {
  const value = englishBusinessType.toLowerCase()

  const rules: Array<{ match: string[]; icon: IconKeyword }> = [
    { match: ["coffee", "cafe", "espresso"], icon: "coffee" },
    { match: ["restaurant", "food", "burger", "pizza"], icon: "utensils" },
    { match: ["bakery", "dessert", "cake"], icon: "cake" },
    { match: ["salon", "beauty", "spa", "barber"], icon: "scissors" },
    { match: ["gym", "fitness", "workout"], icon: "dumbbell" },
    { match: ["pharmacy", "medical", "health"], icon: "cross" },
    { match: ["fashion", "clothes", "boutique"], icon: "shirt" },
    { match: ["perfume", "fragrance"], icon: "sparkles" },
    { match: ["jewelry", "gold", "accessories"], icon: "gem" },
    { match: ["flower", "florist"], icon: "flower" },
    { match: ["grocery", "market", "supermarket"], icon: "shopping-basket" },
    { match: ["electronics", "mobile", "computer"], icon: "smartphone" },
    { match: ["car", "automotive", "service"], icon: "car" },
    { match: ["store", "shop", "retail"], icon: "store" },
  ]

  for (const rule of rules) {
    if (rule.match.some((keyword) => value.includes(keyword))) {
      return rule.icon
    }
  }

  return "store"
}

function normalizeArabicText(value: string): string {
  return value
    .toLowerCase()
    .trim()
    .replace(/[أإآ]/g, "ا")
    .replace(/ة/g, "ه")
    .replace(/ى/g, "ي")
    .replace(/ؤ/g, "و")
    .replace(/ئ/g, "ي")
    .replace(/\s+/g, " ")
}

/* -------------------- icon svg -------------------- */

function buildPointIconDataUrl(
  keyword: IconKeyword,
  color: string,
  width: number,
  height: number
): string {
  const svg = getIconSvgByKeyword(keyword, color, width, height)
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

function getIconSvgByKeyword(
  keyword: IconKeyword,
  color: string,
  width: number,
  height: number
): string {
  const common = `fill="none" stroke="${color}" stroke-width="10" stroke-linecap="round" stroke-linejoin="round"`

  switch (keyword) {
    case "coffee":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M50 80h70v25a35 35 0 0 1-35 35h0a35 35 0 0 1-35-35V80z"/><path d="M120 88h12a16 16 0 0 1 0 32h-12"/><path d="M60 150h80"/><path d="M75 48c0 10-8 12-8 20"/><path d="M100 43c0 12-8 14-8 24"/><path d="M125 48c0 10-8 12-8 20"/></g></svg>`
    case "utensils":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M58 35v60"/><path d="M43 35v30"/><path d="M58 35v30"/><path d="M73 35v30"/><path d="M43 65c0 16 9 30 15 30s15-14 15-30"/><path d="M58 95v70"/><path d="M130 35c-16 18-20 42-20 62v68"/><path d="M130 35c16 18 20 42 20 62"/></g></svg>`
    case "cake":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M45 90h110v55H45z"/><path d="M45 90c8 0 8-10 16-10s8 10 16 10 8-10 16-10 8 10 16 10 8-10 16-10 8 10 16 10"/><path d="M100 45v25"/><path d="M93 45c0-7 14-7 14 0 0 4-3 7-7 10-4-3-7-6-7-10z"/><path d="M40 145h120"/></g></svg>`
    case "scissors":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><circle cx="70" cy="72" r="16"/><circle cx="130" cy="72" r="16"/><path d="M82 84l68 68"/><path d="M118 84L50 152"/><path d="M90 92l-18 18"/><path d="M110 92l18 18"/></g></svg>`
    case "dumbbell":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M60 100h80"/><path d="M75 82v36"/><path d="M125 82v36"/><path d="M45 85v30"/><path d="M30 75v50"/><path d="M155 85v30"/><path d="M170 75v50"/></g></svg>`
    case "cross":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M100 45v110"/><path d="M45 100h110"/></g></svg>`
    case "shirt":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M70 50l30 18 30-18 20 25-18 18v62H68V93L50 75z"/><path d="M82 48h36"/></g></svg>`
    case "gem":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M60 70l25-25h30l25 25-40 85z"/><path d="M60 70h80"/><path d="M85 45l15 25 15-25"/></g></svg>`
    case "flower":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><circle cx="100" cy="95" r="14"/><circle cx="100" cy="60" r="18"/><circle cx="100" cy="130" r="18"/><circle cx="65" cy="95" r="18"/><circle cx="135" cy="95" r="18"/><path d="M100 109v50"/><path d="M100 140c-10 0-18 6-24 14"/></g></svg>`
    case "shopping-basket":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M45 85h110l-10 65H55z"/><path d="M75 85c0-18 8-30 25-30s25 12 25 30"/><path d="M70 105h60"/></g></svg>`
    case "smartphone":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><rect x="65" y="30" width="70" height="140" rx="12"/><path d="M92 48h16"/><circle cx="100" cy="148" r="4"/></g></svg>`
    case "car":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M50 115l12-30h76l12 30"/><path d="M42 115h116v28H42z"/><circle cx="68" cy="145" r="10"/><circle cx="132" cy="145" r="10"/></g></svg>`
    case "sparkles":
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M100 45l10 30 30 10-30 10-10 30-10-30-30-10 30-10z"/><path d="M145 45l5 14 14 5-14 5-5 14-5-14-14-5 14-5z"/><path d="M60 120l6 16 16 6-16 6-6 16-6-16-16-6 16-6z"/></g></svg>`
    case "store":
    default:
      return `<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${height}" viewBox="0 0 200 200"><g ${common}><path d="M45 75h110v78H45z"/><path d="M38 75l10-25h104l10 25"/><path d="M70 153v-35h60v35"/></g></svg>`
  }
}

/* -------------------- color extraction -------------------- */

async function extractSmartBrandColors(img: HTMLImageElement): Promise<BrandColors> {
  const analysis = analyzeLogoColors(img)

  const backgroundRgb = analysis.background
  const accentRgb = analysis.accent ?? getContrastingFallback(backgroundRgb)

  const primary = rgbToHex(backgroundRgb)
  const accent = rgbToHex(accentRgb)
  const secondary = accent
  const textOnPrimary = getReadableTextColor(primary)

  return {
    primary,
    secondary,
    accent,
    textOnPrimary,
    logoPlate: "transparent",
    pointIconColor: primary,
  }
}

function analyzeLogoColors(img: HTMLImageElement): {
  background: RGB
  accent: RGB | null
} {
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")

  if (!ctx) {
    return {
      background: { r: 243, g: 244, b: 246 },
      accent: { r: 31, g: 41, b: 55 },
    }
  }

  const targetWidth = 220
  const scale = targetWidth / (img.naturalWidth || img.width)
  const width = targetWidth
  const height = Math.max(1, Math.round((img.naturalHeight || img.height) * scale))

  canvas.width = width
  canvas.height = height
  ctx.drawImage(img, 0, 0, width, height)

  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  const borderSamples = getBorderSamples(data, width, height)

  const background =
    getExactDominantColor(borderSamples, 10) ??
    estimateAverage(borderSamples) ??
    { r: 243, g: 244, b: 246 }

  const allSamples = getAllOpaqueSamples(data)

  const accent =
    getBestAccentColor(allSamples, background) ??
    getContrastingFallback(background)

  return {
    background,
    accent,
  }
}

function cropLogoPaddingToDataUrl(img: HTMLImageElement): string {
  const canvas = document.createElement("canvas")
  const ctx = canvas.getContext("2d")

  if (!ctx) return img.src

  canvas.width = img.naturalWidth || img.width
  canvas.height = img.naturalHeight || img.height
  ctx.drawImage(img, 0, 0)

  const bg = detectOuterBackgroundColor(canvas)
  const cropped = cropCanvasByBackground(canvas, bg, 14, 2)

  return cropped.toDataURL("image/png")
}

function detectOuterBackgroundColor(canvas: HTMLCanvasElement): RGB {
  const ctx = canvas.getContext("2d")
  if (!ctx) return { r: 255, g: 255, b: 255 }

  const { width, height } = canvas
  const data = ctx.getImageData(0, 0, width, height).data
  const samples = getBorderSamples(data, width, height)

  return (
    getExactDominantColor(samples, 10) ??
    estimateAverage(samples) ??
    { r: 255, g: 255, b: 255 }
  )
}

function cropCanvasByBackground(
  source: HTMLCanvasElement,
  background: RGB,
  threshold: number,
  padding: number
): HTMLCanvasElement {
  const ctx = source.getContext("2d")
  if (!ctx) return source

  const { width, height } = source
  const imageData = ctx.getImageData(0, 0, width, height)
  const data = imageData.data

  let top = height
  let left = width
  let right = 0
  let bottom = 0
  let found = false

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4
      const alpha = data[i + 3]

      if (alpha < 12) continue

      const pixel = {
        r: data[i],
        g: data[i + 1],
        b: data[i + 2],
      }

      const distance = getColorDistance(pixel, background)
      if (distance <= threshold) continue

      found = true
      if (x < left) left = x
      if (x > right) right = x
      if (y < top) top = y
      if (y > bottom) bottom = y
    }
  }

  if (!found) return source

  const finalLeft = Math.max(0, left - padding)
  const finalTop = Math.max(0, top - padding)
  const finalRight = Math.min(width - 1, right + padding)
  const finalBottom = Math.min(height - 1, bottom + padding)

  const finalWidth = finalRight - finalLeft + 1
  const finalHeight = finalBottom - finalTop + 1

  const output = document.createElement("canvas")
  const outputCtx = output.getContext("2d")
  if (!outputCtx) return source

  output.width = finalWidth
  output.height = finalHeight

  outputCtx.drawImage(
    source,
    finalLeft,
    finalTop,
    finalWidth,
    finalHeight,
    0,
    0,
    finalWidth,
    finalHeight
  )

  return output
}

function getExactDominantColor(samples: RGB[], tolerance: number): RGB | null {
  if (!samples.length) return null

  const clusters: Array<{ seed: RGB; pixels: RGB[] }> = []

  for (const sample of samples) {
    const match = clusters.find(
      (cluster) => getColorDistance(cluster.seed, sample) <= tolerance
    )

    if (match) {
      match.pixels.push(sample)
    } else {
      clusters.push({
        seed: sample,
        pixels: [sample],
      })
    }
  }

  clusters.sort((a, b) => b.pixels.length - a.pixels.length)

  const best = clusters[0]
  if (!best) return null

  return averageRgb(best.pixels)
}

function getBestAccentColor(samples: RGB[], background: RGB): RGB | null {
  if (!samples.length) return null

  const reduced = samplePixels(samples, 2500)
  const clusters: Array<{ seed: RGB; pixels: RGB[] }> = []

  for (const sample of reduced) {
    const match = clusters.find(
      (cluster) => getColorDistance(cluster.seed, sample) <= 18
    )

    if (match) {
      match.pixels.push(sample)
    } else {
      clusters.push({
        seed: sample,
        pixels: [sample],
      })
    }
  }

  const valid = clusters
    .map((cluster) => {
      const rgb = averageRgb(cluster.pixels)
      const share = cluster.pixels.length / reduced.length
      const distance = getColorDistance(rgb, background)
      const saturation = getSaturation(rgb)
      const brightness = getBrightness(rgb)

      return {
        rgb,
        share,
        distance,
        saturation,
        brightness,
        score:
          share * 100 +
          distance * 0.9 +
          saturation * 60 -
          Math.abs(brightness - 140) * 0.06,
      }
    })
    .filter(
      (item) =>
        item.share >= 0.015 &&
        item.distance >= 35 &&
        item.saturation >= 0.08
    )
    .sort((a, b) => b.score - a.score)

  return valid[0]?.rgb ?? null
}

function samplePixels(samples: RGB[], maxCount: number): RGB[] {
  if (samples.length <= maxCount) return samples

  const step = samples.length / maxCount
  const output: RGB[] = []

  for (let i = 0; i < maxCount; i++) {
    output.push(samples[Math.floor(i * step)])
  }

  return output
}

function getBorderSamples(
  data: Uint8ClampedArray,
  width: number,
  height: number
): RGB[] {
  const samples: RGB[] = []
  const border = Math.max(2, Math.round(Math.min(width, height) * 0.06))

  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const isBorder =
        x < border || y < border || x >= width - border || y >= height - border

      if (!isBorder) continue

      const i = (y * width + x) * 4
      const a = data[i + 3]
      if (a < 20) continue

      samples.push({
        r: data[i],
        g: data[i + 1],
        b: data[i + 2],
      })
    }
  }

  return samples
}

function getAllOpaqueSamples(data: Uint8ClampedArray): RGB[] {
  const samples: RGB[] = []

  for (let i = 0; i < data.length; i += 4) {
    const a = data[i + 3]
    if (a < 20) continue

    samples.push({
      r: data[i],
      g: data[i + 1],
      b: data[i + 2],
    })
  }

  return samples
}

function getContrastingFallback(background: RGB): RGB {
  return getBrightness(background) > 150
    ? { r: 31, g: 41, b: 55 }
    : { r: 255, g: 255, b: 255 }
}

function estimateAverage(samples: RGB[]): RGB | null {
  if (!samples.length) return null
  return averageRgb(samples)
}

function getSaturation(rgb: RGB): number {
  const r = rgb.r / 255
  const g = rgb.g / 255
  const b = rgb.b / 255

  const max = Math.max(r, g, b)
  const min = Math.min(r, g, b)

  if (max === 0) return 0
  return (max - min) / max
}

/* -------------------- helpers -------------------- */

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error("فشل تحميل الصورة."))
    img.src = src
  })
}

function hexToRgb(hex: string): RGB | null {
  const clean = hex.replace("#", "")
  const full =
    clean.length === 3
      ? clean
          .split("")
          .map((char) => char + char)
          .join("")
      : clean

  const int = Number.parseInt(full, 16)
  if (Number.isNaN(int)) return null

  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  }
}

function rgbToHex(rgb: RGB): string {
  const toHex = (value: number): string =>
    clamp(Math.round(value), 0, 255).toString(16).padStart(2, "0")
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`
}

function getBrightness(rgb: RGB): number {
  return (rgb.r * 299 + rgb.g * 587 + rgb.b * 114) / 1000
}

function getReadableTextColor(backgroundHex: string): string {
  const rgb = hexToRgb(backgroundHex)
  if (!rgb) return "#ffffff"
  return getBrightness(rgb) > 155 ? "#111827" : "#ffffff"
}

function averageRgb(colors: RGB[]): RGB {
  const total = colors.reduce(
    (acc, color) => {
      acc.r += color.r
      acc.g += color.g
      acc.b += color.b
      return acc
    },
    { r: 0, g: 0, b: 0 }
  )

  return {
    r: Math.round(total.r / colors.length),
    g: Math.round(total.g / colors.length),
    b: Math.round(total.b / colors.length),
  }
}

function getColorDistance(a: RGB, b: RGB): number {
  return Math.sqrt(
    Math.pow(a.r - b.r, 2) +
      Math.pow(a.g - b.g, 2) +
      Math.pow(a.b - b.b, 2)
  )
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value))
}